import type { AvatarConfig, AvatarPart } from '../lib/avatar'

type Props = {
  config: AvatarConfig
  onPartClick?: (part: AvatarPart) => void
}

function faceGeometry(config: AvatarConfig) {
  if (config.faceShape === 'oval') return <ellipse cx="24" cy="21" fill={config.skin} rx="10" ry="12" />
  if (config.faceShape === 'soft-square') return <rect fill={config.skin} height="22" rx="7" width="21" x="13.5" y="10" />
  return <circle cx="24" cy="21" fill={config.skin} r="11" />
}

function hairGeometry(config: AvatarConfig) {
  switch (config.hairStyle) {
    case 'crop':
      return <path d="M14 17c0-8 4-12 10-12s10 4 10 12c-4-4-16-4-20 0Z" fill={config.hairColor} />
    case 'curly':
      return <path d="M13 18c-2-2-1-5 1-6-1-3 2-5 4-5 1-3 5-3 7-1 2-2 6 0 6 3 4 0 5 5 3 7 2 2 0 5-1 6-1-4-2-8-4-11-5 4-11 5-16 4Z" fill={config.hairColor} />
    case 'side':
      return <path d="M13 19c-1-9 4-14 12-14 7 0 11 4 11 11-5-4-9-6-16-5-2 3-4 6-7 8Z" fill={config.hairColor} />
    case 'bun':
      return <><circle cx="31" cy="7" fill={config.hairColor} r="5" /><path d="M13 19c-1-9 4-14 12-14 7 0 11 5 10 14-3-5-6-8-11-9-3 4-7 7-11 9Z" fill={config.hairColor} /></>
    default:
      return <path d="M13 20c-1-10 4-15 12-15 8 0 12 6 10 16-2-2-4-6-5-9-4 4-10 6-17 5Z" fill={config.hairColor} />
  }
}

function eyesGeometry(config: AvatarConfig) {
  if (config.eyeStyle === 'happy') {
    return <><path d="M18.5 21c1-2 3-2 4 0" fill="none" stroke={config.eyeColor} strokeLinecap="round" strokeWidth="1.2" /><path d="M25.5 21c1-2 3-2 4 0" fill="none" stroke={config.eyeColor} strokeLinecap="round" strokeWidth="1.2" /></>
  }
  const radius = config.eyeStyle === 'wide' ? 1.45 : 1
  return <><circle cx="20" cy="21" fill={config.eyeColor} r={radius} /><circle cx="28" cy="21" fill={config.eyeColor} r={radius} /></>
}

function mouthGeometry(config: AvatarConfig) {
  if (config.mouthStyle === 'neutral') return <path d="M20.5 26.5h7" fill="none" stroke="#784838" strokeLinecap="round" strokeWidth="1" />
  if (config.mouthStyle === 'grin') return <path d="M19.5 25.5c2 3 7 3 9 0-1 4-8 5-9 0Z" fill="#fff7f0" stroke="#784838" strokeLinejoin="round" strokeWidth=".7" />
  return <path d="M20 26c2 1.5 6 1.5 8 0" fill="none" stroke="#784838" strokeLinecap="round" strokeWidth="1" />
}

function facialHairGeometry(config: AvatarConfig) {
  if (config.facialHair === 'moustache') return <path d="M20 25c1.5-1 3-.8 4 .2 1-1 2.5-1.2 4-.2-.5 2-2.2 2.5-4 1.2-1.8 1.3-3.5.8-4-1.2Z" fill={config.hairColor} opacity=".9" />
  if (config.facialHair === 'stubble') return <path d="M16 25c2 7 14 7 16 0-2 8-14 10-16 0Z" fill={config.hairColor} opacity=".18" />
  return null
}

function accessoryGeometry(config: AvatarConfig) {
  if (config.accessory === 'glasses') {
    return <><rect fill="none" height="5" rx="1.5" stroke="#39413c" strokeWidth="1" width="7" x="16.2" y="18.5" /><rect fill="none" height="5" rx="1.5" stroke="#39413c" strokeWidth="1" width="7" x="24.8" y="18.5" /><path d="M23.2 20.5h1.6M15 20h1.2M31.8 20H33" stroke="#39413c" strokeWidth="1" /></>
  }
  if (config.accessory === 'round-glasses') {
    return <><circle cx="20" cy="21" fill="none" r="3.2" stroke="#39413c" strokeWidth="1" /><circle cx="28" cy="21" fill="none" r="3.2" stroke="#39413c" strokeWidth="1" /><path d="M23.2 21h1.6M15.5 20h1.2M31.3 20h1.2" stroke="#39413c" strokeWidth="1" /></>
  }
  return null
}

export default function AvatarArtwork({ config, onPartClick }: Props) {
  const interactiveStyle = onPartClick ? { cursor: 'pointer' as const } : undefined
  return (
    <>
      <rect fill={config.background} height="48" onClick={() => onPartClick?.('background')} style={interactiveStyle} width="48" />
      <path d="M7 48c1-12 8-17 17-17s16 6 17 17" fill={config.shirtColor} onClick={() => onPartClick?.('shirt')} style={interactiveStyle} />
      <g onClick={() => onPartClick?.('face')} style={interactiveStyle}>{faceGeometry(config)}</g>
      <g onClick={() => onPartClick?.('hair')} style={interactiveStyle}>{hairGeometry(config)}</g>
      <g onClick={() => onPartClick?.('eyes')} style={interactiveStyle}>{eyesGeometry(config)}</g>
      <g onClick={() => onPartClick?.('mouth')} style={interactiveStyle}>{mouthGeometry(config)}</g>
      <g onClick={() => onPartClick?.('facialHair')} style={interactiveStyle}>{facialHairGeometry(config)}</g>
      <g onClick={() => onPartClick?.('accessory')} style={interactiveStyle}>{accessoryGeometry(config)}</g>
    </>
  )
}
